/**
 * Default start date of a wholesale line, worked out from size availability.
 *
 * Agreed on 2026-09-29: a new line no longer starts today. Sizes are taken in
 * order of their available date and counted until the share of sizes covered
 * reaches the threshold, and that size's date becomes the default start date.
 * A date already past becomes today. Example: 40% of the sizes available Oct 1
 * and 40% more Oct 15 gives Oct 15, because that is where 80% is covered.
 *
 * The share counts sizes, not pairs: the sizes being ordered once quantities
 * are entered, the line's whole size run before that.
 *
 * Nothing is imported here on purpose, so the rule stays one plain function the
 * grid, the cell editors and the dialogs all read.
 */

// share of a line's sizes (1-100) that has to be available on the start date
export const WHOLESALE_START_DATE_SIZE_SHARE = 80

const ISO_DAY = /^(\d{4}-\d{2}-\d{2})/

// Order Pro sends availabilityDate as a string, either a day or a timestamp
function dayOf (value) {
  const match = typeof value === 'string' ? ISO_DAY.exec(value) : null
  return match ? match[1] : null
}

/**
 * The availability based start date as 'YYYY-MM-DD', or null when it cannot be
 * worked out: no availability, no dates on it, or a share the dated sizes never
 * reach. The caller then keeps its own default, which is today.
 *
 * @param availability array of { size, availabilityDate }, as the line carries it
 * @param today        the browser's own day as 'YYYY-MM-DD'
 * @param quantities   the line's sizes object, { [size]: quantity }
 * @param share        share of sizes to cover, 1-100
 */
export function wholesaleAvailabilityStartDate (availability, { today = null, quantities = null, share = WHOLESALE_START_DATE_SIZE_SHARE } = {}) {
  if (!availability?.length || !(share > 0)) return null

  // once quantities are entered, only the sizes being ordered count
  const ordered = quantities ? availability.filter(item => Number(quantities[item.size]) > 0) : []
  const sizes = ordered.length ? ordered : availability

  const needed = Math.ceil((sizes.length * Math.min(share, 100)) / 100)
  const dates = sizes
    .map(item => dayOf(item.availabilityDate))
    .filter(day => day !== null)
    .sort()
  if (dates.length < needed) return null

  const date = dates[needed - 1]
  return (today && date < today) ? today : date
}
